/**
 * usePositionLevelMap Hook
 *
 * 직위(Position Level) 데이터를 id → PositionLevel 형태로 매핑하여 제공하는 Hook입니다.
 *
 * **용도:**
 * - 진급 후보 산출 시 빠른 직위 조회
 * - 반복적인 find() 연산 최적화
 *
 * **성능 최적화:**
 * - O(n) → O(1) 조회 성능 향상
 * - useMemo로 매핑 결과 캐싱
 *
 * **사용 예시:**
 * ```tsx
 * const { positionLevelMap, positionLevels, isLoading } = usePositionLevelMap();
 *
 * // 빠른 조회
 * const currentPosition = positionLevelMap[employee.positionId];
 *
 * // 다음 직위 찾기
 * const nextPosition = positionLevels.find(p => p.order === currentPosition.order + 1);
 * ```
 */

import { useMemo } from 'react';
import { usePositionLevels } from './usePositionLevels';
import { PositionLevel } from '../types/positionLevel';

/**
 * usePositionLevelMap Hook 반환 타입
 */
export interface UsePositionLevelMapResult {
  /**
   * 직위 ID → PositionLevel 매핑 객체
   *
   * 예시:
   * {
   *   'pos_staff': { id: 'pos_staff', name: '사원', order: 1, ... },
   *   'pos_assistant': { id: 'pos_assistant', name: '대리', order: 2, ... },
   *   ...
   * }
   */
  positionLevelMap: Record<string, PositionLevel>;

  /**
   * 전체 직위 목록 (order로 정렬됨)
   */
  positionLevels: PositionLevel[];

  /**
   * 로딩 중 여부
   */
  isLoading: boolean;

  /**
   * 에러
   */
  error: Error | null;

  /**
   * 수동 새로고침 함수
   */
  refetch: () => void;
}

/**
 * 직위 ID → PositionLevel 매핑 Hook
 *
 * @returns UsePositionLevelMapResult
 */
export function usePositionLevelMap(): UsePositionLevelMapResult {
  // 전체 직위 목록 조회 (활성 직위만)
  const { positionLevels, isLoading, error, refetch } = usePositionLevels({
    status: 'active',
  });

  /**
   * 직위 ID → PositionLevel 매핑 객체 생성
   *
   * useMemo를 사용하여 positionLevels가 변경될 때만 재계산합니다.
   * 이를 통해 불필요한 객체 재생성을 방지하고 성능을 최적화합니다.
   */
  const positionLevelMap = useMemo(() => {
    const map: Record<string, PositionLevel> = {};

    positionLevels.forEach(positionLevel => {
      map[positionLevel.id] = positionLevel;
    });

    return map;
  }, [positionLevels]);

  return {
    positionLevelMap,
    positionLevels,
    isLoading,
    error,
    refetch,
  };
}

/**
 * ============================================
 * 성능 최적화 효과
 * ============================================
 *
 * **Before (find 사용):**
 * ```ts
 * // 진급 후보 100명 계산 시
 * for (const employee of employees) {
 *   const currentPosition = positionLevels.find(
 *     p => p.id === employee.positionId
 *   ); // O(n) - 평균 5회 순회
 *   // ...
 * }
 * // 총 연산: 100 * 5 = 500회 순회
 * ```
 *
 * **After (Map 사용):**
 * ```ts
 * // 진급 후보 100명 계산 시
 * for (const employee of employees) {
 *   const currentPosition = positionLevelMap[employee.positionId]; // O(1)
 *   // ...
 * }
 * // 총 연산: 100 * 1 = 100회 조회
 * ```
 *
 * **성능 향상:**
 * - 직원 100명 기준: 500회 → 100회 (80% 감소)
 * - 직원 1000명 기준: 5000회 → 1000회 (80% 감소)
 *
 * **메모리:**
 * - 직위 10개 기준: 약 1-2KB 추가 메모리 (무시할 수 있는 수준)
 */

/**
 * ============================================
 * 사용 예시
 * ============================================
 *
 * **예시 1: 진급 후보 산출**
 * ```ts
 * function usePromotionCandidates(filters) {
 *   const { positionLevelMap, positionLevels } = usePositionLevelMap();
 *   const { employees } = useEmployees();
 *
 *   const candidates = employees.map(employee => {
 *     // O(1) 조회
 *     const currentPosition = positionLevelMap[employee.positionId];
 *
 *     // O(n) 조회 (전체 직위 수는 적으므로 괜찮음)
 *     const nextPosition = positionLevels.find(
 *       p => p.order === currentPosition.order + 1
 *     );
 *
 *     // 진급 기준 계산...
 *     return { employee, currentPosition, nextPosition, ... };
 *   });
 *
 *   return { candidates, ... };
 * }
 * ```
 *
 * **예시 2: 직원 목록에서 직위명 표시**
 * ```ts
 * function EmployeeList() {
 *   const { employees } = useEmployees();
 *   const { positionLevelMap } = usePositionLevelMap();
 *
 *   return (
 *     <table>
 *       {employees.map(emp => (
 *         <tr key={emp.id}>
 *           <td>{emp.nameKo}</td>
 *           <td>
 *             {positionLevelMap[emp.positionId]?.name || '-'}
 *           </td>
 *         </tr>
 *       ))}
 *     </table>
 *   );
 * }
 * ```
 *
 * **예시 3: 조직도에서 직위별 정렬**
 * ```ts
 * function OrgTree() {
 *   const { members } = useOrgMembers();
 *   const { positionLevelMap } = usePositionLevelMap();
 *
 *   // 직위별 정렬 (rankWeight 기준)
 *   const sortedMembers = [...members].sort((a, b) => {
 *     const aWeight = positionLevelMap[a.positionId]?.rankWeight || 0;
 *     const bWeight = positionLevelMap[b.positionId]?.rankWeight || 0;
 *     return bWeight - aWeight; // 높은 직위 우선
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */

/**
 * ============================================
 * 향후 확장 가능성
 * ============================================
 *
 * **1. 캐싱 강화 (React Query 사용):**
 * ```ts
 * import { useQuery } from 'react-query';
 *
 * export function usePositionLevelMap() {
 *   const { data, isLoading, error, refetch } = useQuery(
 *     'positionLevels',
 *     fetchPositionLevels,
 *     {
 *       staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
 *       cacheTime: 30 * 60 * 1000, // 30분간 캐시 보관
 *     }
 *   );
 *
 *   const positionLevelMap = useMemo(() => {
 *     // ... 매핑 로직
 *   }, [data]);
 *
 *   return { positionLevelMap, ... };
 * }
 * ```
 *
 * **2. 역방향 매핑 추가:**
 * ```ts
 * const positionLevelByName = useMemo(() => {
 *   const map: Record<string, PositionLevel> = {};
 *   positionLevels.forEach(p => {
 *     map[p.name] = p;
 *   });
 *   return map;
 * }, [positionLevels]);
 *
 * // 사용
 * const position = positionLevelByName['과장'];
 * ```
 *
 * **3. order 기반 매핑 추가:**
 * ```ts
 * const positionLevelByOrder = useMemo(() => {
 *   const map: Record<number, PositionLevel> = {};
 *   positionLevels.forEach(p => {
 *     map[p.order] = p;
 *   });
 *   return map;
 * }, [positionLevels]);
 *
 * // 사용 (다음 직위 빠른 조회)
 * const nextPosition = positionLevelByOrder[currentPosition.order + 1];
 * ```
 */
